// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * Derived takeoff angles for links that would otherwise render on top of
 * one another.  See issue #12.
 *
 * Two links between the same pair of variables, neither carrying authored
 * geometry, resolve to the same straight segment between the same two
 * element boundaries.  They coincide exactly, and whichever paints last
 * owns the only visible arrowhead.  A two-variable feedback loop -- the
 * most common shape in a causal loop diagram -- therefore draws as a
 * single line, and the loop is computed correctly but invisible.
 *
 * Neither of the two places that decide link geometry can see this.
 * `linkViewElementFromJson` looks at one link's JSON; `Connector` looks at
 * one link's endpoints.  The collision is a property of the *set*, so it
 * takes a pass over the set to notice.  That is all this module is.
 *
 * Three constraints from the issue, all load-bearing:
 *
 *   1. **Derived, never persisted.**  The caller applies these angles to a
 *      copy of the element on its way to the renderer.  Writing one back
 *      into `LinkViewElement.arc` would mean an author who later drags the
 *      link fights a value they never set, and every model would acquire
 *      spurious view diffs the first time it was opened.
 *   2. **An authored arc always wins.**  A link with explicit geometry is
 *      never given a derived angle, and never counted as colliding -- it is
 *      already curved, so it no longer coincides with anything straight.
 *   3. **Nothing is computed for a link being dragged.**  The caller
 *      excludes those; see the drag invariant in `src/diagram/CLAUDE.md`.
 */

import type { UID } from '@simlin/core/datamodel';

/**
 * Default angular step, in degrees, between a link and the direct
 * source-to-target line.
 *
 * Must exceed `StraightLineMax` (6 degrees, `drawing/default.ts`) or
 * `Connector` will classify the result as straight and the separation will
 * not happen.  30 degrees is comfortably clear of that and reads as a
 * deliberate bow rather than a wobble.
 */
export const DefaultSeparationDeg = 30;

/** A link reduced to what separation needs to know about it. */
export interface SeparableLink {
  uid: UID;
  fromUid: UID;
  toUid: UID;
  /**
   * True when the link carries an authored `arc` or `multiPoint`.  Such a
   * link is left alone and does not participate in a collision.
   */
  hasExplicitGeometry: boolean;
  /**
   * Angle of the direct source-to-target line, in degrees, in the same
   * frame `Connector` uses: `atan2(to.cy - from.cy, to.cx - from.cx)`.
   */
  midThetaDeg: number;
}

/** Unordered endpoint key, so A->B and B->A land in the same group. */
function pairKey(a: UID, b: UID): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

/**
 * Compute a derived takeoff angle for every link that would otherwise be
 * drawn coincident with another.
 *
 * Returns a map of `uid -> takeoff angle in degrees`, containing entries
 * *only* for links that need one.  A link absent from the map should be
 * rendered exactly as it is today.
 *
 * The offset is applied to each link's own `midThetaDeg`, which is what
 * makes the reciprocal case work without any special-casing: `A->B` and
 * `B->A` have direct angles 180 degrees apart, so adding the *same* signed
 * offset to both bows them to opposite sides of the shared axis.
 *
 * Duplicate same-direction links are handled by the same grouping, since
 * they share a `midThetaDeg` and so need distinct multiples rather than a
 * shared one.  Members are fanned per direction: the n-th link in a given
 * direction gets `(n + 1) * step`.  For the common case -- one link each
 * way -- both are the first in their direction and both get `step`, which
 * is the reciprocal behaviour above.
 *
 * Ordering is by `uid` within each direction so the result is a function of
 * the view and not of element order, which is not stable across edits.
 */
export function computeLinkSeparation(
  links: readonly SeparableLink[],
  stepDeg: number = DefaultSeparationDeg,
): Map<UID, number> {
  const groups = new Map<string, SeparableLink[]>();
  for (const link of links) {
    // Self-links have no meaningful "other side" to bow toward and are not
    // what this is for.
    if (link.fromUid === link.toUid) {
      continue;
    }
    if (link.hasExplicitGeometry) {
      continue;
    }
    const key = pairKey(link.fromUid, link.toUid);
    const group = groups.get(key);
    if (group) {
      group.push(link);
    } else {
      groups.set(key, [link]);
    }
  }

  const result = new Map<UID, number>();
  for (const group of groups.values()) {
    // A lone link between a pair collides with nothing; leave it straight.
    if (group.length < 2) {
      continue;
    }

    // Fan within each direction. Two links pointing opposite ways are each
    // first in their own direction, so both receive `stepDeg` and bow apart.
    const seenPerDirection = new Map<string, number>();
    const ordered = [...group].sort((a, b) => a.uid - b.uid);
    for (const link of ordered) {
      const dir = `${link.fromUid}->${link.toUid}`;
      const n = seenPerDirection.get(dir) ?? 0;
      seenPerDirection.set(dir, n + 1);
      result.set(link.uid, link.midThetaDeg + (n + 1) * stepDeg);
    }
  }

  return result;
}
